import { css } from 'lit';

export const LIT_CSS_VARIABLES = css`
  :host {
    --default-rem-px-count: 16;
    --top-padding-height: calc(20rem / var(--default-rem-px-count));
    --bottom-padding-height: calc(20rem / var(--default-rem-px-count));
    /*
      Every block brackets its grid rows with this pair — heroes included.
      Standard is 50 mobile / 60 from 600px on both ends; the CMS can raise
      the top to 80 / 100 per block, which is what --block-top-padding-large
      holds. The Figma templates vary here and the standard wins unless a
      block is called out.

      The mobile row gap lands around the padding tracks as well as between
      content rows, so it is subtracted here — the tracks are short by that
      much and the gap makes up the difference. A block that opts out of the
      gap sets --block-row-gap: 0 on its own :host and the arithmetic still
      holds.
    */
    --block-top-padding: calc(
      calc(50rem / var(--default-rem-px-count)) - var(--block-row-gap)
    );
    --block-bottom-padding: calc(
      calc(50rem / var(--default-rem-px-count)) - var(--block-row-gap)
    );
    --block-top-padding-large: calc(
      calc(80rem / var(--default-rem-px-count)) - var(--block-row-gap)
    );

    /* Graphik type scale (mobile values; desktop overrides at 600px).
       Source of truth: Figma "Text Styles Updated" (node 16231:27015). */
    --font-family: '3form Graphik', Helvetica, sans-serif;
    --fw-regular: 400;
    --fw-medium: 500;
    --fs-display: calc(20rem / var(--default-rem-px-count));
    --lh-display: calc(26rem / var(--default-rem-px-count));
    --ls-display: -0.6px;
    --fs-headline: calc(18rem / var(--default-rem-px-count));
    --lh-headline: calc(22rem / var(--default-rem-px-count));
    --ls-headline: -0.24px;
    --fs-title: calc(16rem / var(--default-rem-px-count));
    --lh-title: calc(20rem / var(--default-rem-px-count));
    --ls-title: -0.15px;
    --fs-title-small: calc(16rem / var(--default-rem-px-count));
    --lh-title-small: calc(20rem / var(--default-rem-px-count));
    --ls-title-small: -0.15px;
    --fs-label: calc(14rem / var(--default-rem-px-count));
    --lh-label: calc(21rem / var(--default-rem-px-count));
    --ls-label: -0.18px;
    --fs-label-small: calc(14rem / var(--default-rem-px-count));
    --lh-label-small: calc(21rem / var(--default-rem-px-count));
    --ls-label-small: -0.18px;
    --fs-body: calc(14rem / var(--default-rem-px-count));
    --lh-body: calc(21rem / var(--default-rem-px-count));
    --ls-body: -0.18px;
    --fs-body-small: calc(14rem / var(--default-rem-px-count));
    --lh-body-small: calc(21rem / var(--default-rem-px-count));
    --ls-body-small: -0.18px;
    --fs-caption: calc(10rem / var(--default-rem-px-count));
    --lh-caption: calc(13rem / var(--default-rem-px-count));
    --ls-caption: -0.18px;
    --fs-cta: calc(14rem / var(--default-rem-px-count));
    --lh-cta: calc(17.5rem / var(--default-rem-px-count));
    --ls-cta: -0.24px;
    --fs-cta-link: calc(12rem / var(--default-rem-px-count));
    --lh-cta-link: calc(16rem / var(--default-rem-px-count));
    --ls-cta-link: -0.15px;

    --column-width: minmax(
      calc(100% / 320 * 37),
      calc(
        calc(320rem - (10 * var(--between-column-padding)) / 6) /
          var(--default-rem-px-count)
      )
    );
    --outer-padding: minmax(calc(2rem / var(--default-rem-px-count)), auto);
    --between-column-padding: calc(8rem / var(--default-rem-px-count));
    --between-row-padding: calc(20rem / var(--default-rem-px-count));
    /* Larger spacing pair used by image-grid blocks (grows at 1024px). */
    --between-row-padding-lg: calc(30rem / var(--default-rem-px-count));
    --between-column-padding-lg: calc(24rem / var(--default-rem-px-count));
    /* The shared grid's mobile row gap, zero from 600px up. Named so blocks
       can subtract it where a gap below is measured across a row boundary. */
    --block-row-gap: calc(15rem / var(--default-rem-px-count));
    /* Mobile text stack: header -> description -> CTAs. Blocks that stack
       those three share this pair so the convention has one source of truth.
       Figma Boyle mobile, node 21180:3582. Where header and description sit
       in separate grid rows, subtract --block-row-gap from the header value.
       Where they sit in the same box, use the token as-is. */
    --text-stack-header-gap: calc(30rem / var(--default-rem-px-count));
    --text-stack-cta-gap: calc(40rem / var(--default-rem-px-count));
    --empty-row: '. . . . . . . .';
    --block-line-break: '. line-break line-break line-break line-break line-break line-break .';
    --images-row-with-gutter: '. images images images images images images .';
    --images-row-with-left-gutter: '. images images images images images images images';

    --grey-dark: #b2b2b2;

    /* Block theming. --block-text-color / --block-bg-color are set inline on
       the block host by block_theme_style when the CMS picks a non-white
       background; everything below derives from that pair, so a themed block
       needs no per-block overrides. Usages read them as
       var(--block-text-color, <original literal>) so an untinted block keeps
       its original color even where these are unset. */
    --block-pressed-solid: #979797;
    --block-pressed-tint: #e8e8e8;
    --block-rule-color: #c0c0c0;
  }

  @supports (color: color-mix(in srgb, red, blue)) {
    :host {
      /* Ratios chosen to land back on the literals above for a default
         (#222 on #ffffff) block. */
      --block-pressed-solid: color-mix(
        in srgb,
        var(--block-bg-color, #ffffff) 53%,
        var(--block-text-color, #222222)
      );
      --block-pressed-tint: color-mix(
        in srgb,
        var(--block-bg-color, #ffffff) 90%,
        var(--block-text-color, #222222)
      );
      --block-rule-color: color-mix(
        in srgb,
        var(--block-bg-color, #ffffff) 71.5%,
        var(--block-text-color, #222222)
      );
    }
  }

  @media (min-width: 600px) {
    :host {
      --fs-display: calc(40rem / var(--default-rem-px-count));
      --lh-display: calc(48rem / var(--default-rem-px-count));
      --ls-display: -1.6px;
      --fs-headline: calc(28rem / var(--default-rem-px-count));
      --lh-headline: calc(40rem / var(--default-rem-px-count));
      --ls-headline: -1.12px;
      --fs-title: calc(24rem / var(--default-rem-px-count));
      --lh-title: calc(30rem / var(--default-rem-px-count));
      --ls-title: -0.72px;
      --fs-title-small: calc(18rem / var(--default-rem-px-count));
      --lh-title-small: calc(24rem / var(--default-rem-px-count));
      --ls-title-small: -0.72px;
      --fs-label: calc(16rem / var(--default-rem-px-count));
      --lh-label: calc(24rem / var(--default-rem-px-count));
      --ls-label: -0.24px;
      --fs-label-small: calc(14rem / var(--default-rem-px-count));
      --lh-label-small: calc(20rem / var(--default-rem-px-count));
      --ls-label-small: 0px;
      --fs-body: calc(16rem / var(--default-rem-px-count));
      --lh-body: calc(24rem / var(--default-rem-px-count));
      --ls-body: -0.24px;
      --fs-body-small: calc(14rem / var(--default-rem-px-count));
      --lh-body-small: calc(21rem / var(--default-rem-px-count));
      --ls-body-small: -0.24px;
      --fs-caption: calc(10.5rem / var(--default-rem-px-count));
      --lh-caption: calc(14rem / var(--default-rem-px-count));
      --ls-caption: -0.15px;

      --top-padding-height: calc(40rem / var(--default-rem-px-count));
      --bottom-padding-height: calc(50rem / var(--default-rem-px-count));
      --block-top-padding: calc(60rem / var(--default-rem-px-count));
      --block-bottom-padding: calc(60rem / var(--default-rem-px-count));
      --block-top-padding-large: calc(100rem / var(--default-rem-px-count));
      --block-row-gap: 0px;
      --between-column-padding: calc(12rem / var(--default-rem-px-count));
      --empty-row: '. . . . . . . . . . . . . .';
      --block-line-break: '. line-break line-break line-break line-break line-break line-break line-break line-break line-break line-break line-break line-break .';
      --text-header-with-cta: '. header header header . description description description description description . cta cta .';
      --images-row-with-gutter: '. images images images images images images images images images images images images .';
      --images-row-with-left-gutter: '. images images images images images images images images images images images images images';
      --column-width: minmax(
        calc(100% / 800 * 45),
        calc(
          calc(800rem - (12 * var(--between-column-padding)) / 12) /
            var(--default-rem-px-count)
        )
      );
      --between-column-padding: calc(8rem / var(--default-rem-px-count));
      --outer-padding: minmax(calc(4rem / var(--default-rem-px-count)), auto);
    }
  }

  @media (min-width: 800px) {
    :host {
      --between-column-padding: calc(18rem / var(--default-rem-px-count));
      --text-header-with-cta: '. header header header . description description description description description . cta cta .';
      --column-width: minmax(
        calc(100% / 1024 * 60),
        calc(
          calc(1024rem - (12 * var(--between-column-padding)) / 12) /
            var(--default-rem-px-count)
        )
      );
    }
  }

  @media (min-width: 1024px) {
    :host {
      --top-padding-height: calc(60rem / var(--default-rem-px-count));
      --bottom-padding-height: calc(80rem / var(--default-rem-px-count));
      --between-column-padding: calc(24rem / var(--default-rem-px-count));
      --between-row-padding-lg: calc(40rem / var(--default-rem-px-count));
      --between-column-padding-lg: calc(32rem / var(--default-rem-px-count));
      --text-header-with-cta: '. header header header . description description description description . . cta cta .';
      --column-width: minmax(
        calc(100% / 1440 * 70),
        calc(
          calc(1440rem - (12 * var(--between-column-padding)) / 12) /
            var(--default-rem-px-count)
        )
      );
    }
  }

  @media (min-width: 1200px) {
    :host {
      --between-column-padding: calc(32rem / var(--default-rem-px-count));
    }
  }

  :host {
    font-family: var(--font-family);
  }

  :host([hidden]) {
    max-width: 1440px;
    margin: 0 auto;
  }
`;

export const GRID_LAYOUT = css`
  .block-grid {
    display: inline-grid;
    grid-template-columns:
      var(--outer-padding)
      repeat(6, 1fr)
      var(--outer-padding);
    gap: var(--block-row-gap) var(--between-column-padding);
  }

  @media (min-width: 600px) {
    .block-grid {
      grid-template-columns:
        var(--outer-padding)
        repeat(12, 1fr)
        var(--outer-padding);

      gap: 0 var(--between-column-padding);
    }
  }

  /* Standard nested subgrid: aligns children to the same 12 columns as the
     desktop block-grid. Span children with grid-column: span N. */
  .nested-grid-12 {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: var(--between-row-padding) var(--between-column-padding);
  }
`;

export const DIALOG_STYLES = css`
  dialog {
    border: none;
    padding: none;
  }

  ::backdrop, /* for native <dialog> */
  dialog + .backdrop {
    /* for dialog-polyfill */
    background-color: rgba(0, 0, 0, 0.4);
  }

  dialog > #dialog-content {
    display: flex;
    flex-direction: column;
    max-width: 100%;
    padding: calc(32rem / var(--default-rem-px-count));
  }

  dialog > #dialog-content > .dialog-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: calc(40rem / var(--default-rem-px-count));
  }

  dialog > #dialog-content > .dialog-header > h3 {
    font-weight: 500;
    font-size: calc(20rem / var(--default-rem-px-count));
  }

  dialog > #dialog-content > .dialog-header > .close-btn {
    cursor: pointer;
  }

  dialog > #dialog-content > .dialog-body {
    display: flex;
    flex-direction: column;
    gap: calc(24rem / var(--default-rem-px-count));
  }

  dialog > #dialog-content > .dialog-body > .dialog-images {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: calc(20rem / var(--default-rem-px-count));
    padding-bottom: calc(20rem / var(--default-rem-px-count));
  }

  dialog
    > #dialog-content
    > .dialog-body
    > .dialog-images
    > .dialog-image-wrapper
    > img {
    max-width: 100%;
  }

  dialog
    > #dialog-content
    > .dialog-body
    > .dialog-images
    > .dialog-image-wrapper
    > :not(:last-child) {
    padding-bottom: calc(14rem / var(--default-rem-px-count));
  }
`;
